
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/utils/languageUtils';

const HeroSection: React.FC = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    // Animation for elements fading in on page load - optimized timing
    const animateElements = document.querySelectorAll('.animate-on-load');
    animateElements.forEach((element, index) => {
      setTimeout(() => {
        element.classList.add('opacity-100', 'translate-y-0');
      }, 100 * index); // Reduced from 200ms to 100ms
    });
  }, []);

  // Scroll to performance section when "Track our Performance" is clicked
  const scrollToPerformance = () => {
    const performanceSection = document.getElementById('performance-section');
    if (performanceSection) {
      performanceSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 to-indigo-950">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/background_images/home_hero.jpg')] bg-cover bg-center opacity-40"></div>
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/70 to-gray-900"></div>
      
      {/* Purple glow effect */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl"></div>
      
      {/* Content */}
      <div className="container relative z-10 px-4 mx-auto text-center py-20 mt-16">
        <img
          src="/logo_1_inverted_no_background.png"
          alt="The Club Logo"
          className="mx-auto h-24 md:h-32 mb-6 opacity-0 translate-y-2 transition-all duration-500 ease-in-out animate-on-load will-change-transform"
        />
        
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-white leading-tight opacity-0 translate-y-2 transition-all duration-500 ease-in-out animate-on-load will-change-transform">
          {t('hero.headline')}
        </h1>
        
        <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-10 opacity-0 translate-y-2 transition-all duration-500 ease-in-out animate-on-load will-change-transform">
          {t('hero.subheadline')}
        </p>
        
        <div className="flex flex-wrap justify-center gap-4 opacity-0 translate-y-2 transition-all duration-500 ease-in-out animate-on-load will-change-transform">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-8 py-3 rounded-full transition-all transform hover:scale-105 shadow-lg"
            asChild
          >
            <Link to={getLocalizedPath('/sign-up', language)}>
              {t('hero.cta.join')}
            </Link>
          </Button>
          
          <Button
            size="lg" 
            className="inline-flex items-center px-6 py-3 bg-transparent border border-purple-500 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all duration-300 animate-on-scroll"
            onClick={scrollToPerformance}
          >
            {t('hero.cta.track')}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </Button>
        </div>
      </div>
      
      {/* Floating data visualization elements */}
      <div className="absolute top-1/3 right-[5%] w-24 h-24 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 shadow-xl transform rotate-6 opacity-0 animate-on-load transition-all duration-600 ease-in-out animate-delay-200 will-change-transform">
        <div className="h-2 w-3/4 bg-green-400/70 rounded-full mb-2"></div>
        <div className="h-2 w-1/2 bg-blue-400/70 rounded-full mb-2"></div>
        <div className="h-2 w-5/6 bg-purple-400/70 rounded-full"></div>
      </div>
      
      <div className="absolute bottom-1/3 left-[10%] w-32 h-32 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 shadow-xl transform -rotate-12 opacity-0 animate-on-load transition-all duration-600 ease-in-out animate-delay-300 will-change-transform">
        <div className="flex justify-between items-center mb-2">
          <div className="h-2 w-8 bg-white/40 rounded-full"></div>
          <div className="h-4 w-4 bg-green-400/70 rounded-full"></div>
        </div>
        <div className="h-12 w-full bg-white/5 rounded-lg mb-2"></div>
        <div className="flex justify-between">
          <div className="h-2 w-6 bg-white/40 rounded-full"></div>
          <div className="h-2 w-10 bg-white/40 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
